import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Flame,
  Zap,
  Droplets,
  Wifi,
  Home,
  Shield,
  Trash2,
  CreditCard,
  Building,
  Phone,
  Package,
  Plus,
  Pencil,
  ArrowDownCircle,
  HandCoins,
  ChevronDown,
  ChevronRight,
  CalendarClock,
  CircleOff,
  TrendingUpDown,
} from 'lucide-react';
import { toast } from 'sonner';

const TYPE_CONFIG = {
  gas:          { label: 'Gas',          icon: Flame,    color: 'text-orange-500' },
  electricity:  { label: 'Electricity',  icon: Zap,      color: 'text-yellow-500' },
  water:        { label: 'Water',        icon: Droplets, color: 'text-blue-500' },
  internet:     { label: 'Internet',     icon: Wifi,     color: 'text-purple-500' },
  rent:         { label: 'Rent',         icon: Home,     color: 'text-rose-500' },
  insurance:    { label: 'Insurance',    icon: Shield,   color: 'text-green-500' },
  waste:        { label: 'Waste',        icon: Trash2,   color: 'text-stone-500' },
  subscription: { label: 'Subscription', icon: CreditCard, color: 'text-indigo-500' },
  rates:        { label: 'Business Rates', icon: Building, color: 'text-slate-500' },
  phone:        { label: 'Phone',        icon: Phone,    color: 'text-teal-500' },
  other:        { label: 'Other',        icon: Package,  color: 'text-gray-500' },
};

const PAYMENT_METHODS = [
  { value: 'direct_debit', label: 'Direct Debit', auto: true },
  { value: 'standing_order', label: 'Standing Order', auto: true },
  { value: 'bank_transfer', label: 'Bank Transfer', auto: false },
  { value: 'card', label: 'Card Payment', auto: false },
  { value: 'cash', label: 'Cash', auto: false },
  { value: 'cheque', label: 'Cheque', auto: false },
  { value: 'other', label: 'Other', auto: false },
];

const FREQUENCIES = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'annually', label: 'Annually' },
];

const formatCurrency = (v) => {
  if (v == null) return '£0.00';
  return `£${Number(v).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const FixedCostsTab = () => {
  const { currentLocation, api } = useAuth();
  const [costs, setCosts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(null);
  const [form, setForm] = useState({
    provider: '', type: 'other', amount: '', frequency: 'monthly',
    payment_day: '1', payment_method: 'direct_debit', notes: '',
    start_date: new Date().toISOString().slice(0, 10),
    end_date: '',
    is_variable: false,
  });

  const fetchData = useCallback(async () => {
    if (!currentLocation) return;
    setLoading(true);
    try {
      const [costsRes, summaryRes] = await Promise.all([
        api.get('/fixed-costs', { params: { location_id: currentLocation.id } }),
        api.get('/fixed-costs/summary', { params: { location_id: currentLocation.id } }),
      ]);
      setCosts(costsRes.data);
      setSummary(summaryRes.data);
    } catch {
      toast.error('Failed to load fixed costs');
    } finally {
      setLoading(false);
    }
  }, [api, currentLocation]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const resetForm = () => setForm({
    provider: '', type: 'other', amount: '', frequency: 'monthly',
    payment_day: '1', payment_method: 'direct_debit', notes: '',
    start_date: new Date().toISOString().slice(0, 10),
    end_date: '',
    is_variable: false,
  });

  const openAdd = () => { resetForm(); setEditing(null); setDialogOpen(true); };

  const openEdit = (c) => {
    setEditing(c);
    setForm({
      provider: c.provider, type: c.type, amount: String(c.amount),
      frequency: c.frequency, payment_day: String(c.payment_day),
      payment_method: c.payment_method || 'other', notes: c.notes || '',
      start_date: c.start_date || '',
      end_date: c.end_date || '',
      is_variable: c.is_variable || false,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.provider || !form.amount) {
      toast.error('Provider and amount are required');
      return;
    }
    try {
      const payload = {
        ...form,
        end_date: form.end_date || null,
      };
      if (editing) {
        await api.put(`/fixed-costs/${editing.id}`, payload);
        toast.success('Fixed cost updated');
      } else {
        await api.post('/fixed-costs', { ...payload, location_id: currentLocation.id });
        toast.success('Fixed cost added');
      }
      setDialogOpen(false);
      fetchData();
    } catch {
      toast.error('Failed to save');
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/fixed-costs/${id}`);
      toast.success('Fixed cost deactivated');
      fetchData();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const getTypeConfig = (type) => TYPE_CONFIG[type] || TYPE_CONFIG.other;

  if (loading) {
    return <div className="text-center py-12 text-muted-foreground">Loading fixed costs...</div>;
  }

  return (
    <div className="space-y-6" data-testid="fixed-costs-tab">
      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Monthly Total</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold" data-testid="monthly-total">{formatCurrency(summary.total_monthly)}</div>
              <p className="text-xs text-muted-foreground">{formatCurrency(summary.total_annual)}/year</p>
              {summary.estimated_monthly > 0 && (
                <p className="text-xs text-purple-600 mt-0.5" data-testid="estimated-note">
                  <TrendingUpDown className="w-3 h-3 inline mr-0.5" />
                  ~{formatCurrency(summary.estimated_monthly)} estimated
                </p>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Active Bills</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{summary.count}</div>
              <p className="text-xs text-muted-foreground">recurring costs</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Auto-Paid</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600" data-testid="auto-paid-count">{summary.auto_paid}</div>
              <p className="text-xs text-muted-foreground">DD / Standing Order</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Action Needed</CardTitle>
            </CardHeader>
            <CardContent>
              <div className={`text-2xl font-bold ${summary.action_needed > 0 ? 'text-amber-600' : 'text-green-600'}`} data-testid="action-needed-count">
                {summary.action_needed}
              </div>
              <p className="text-xs text-muted-foreground">manual payments</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Cost Breakdown by Type */}
      {summary && Object.keys(summary.by_type).length > 0 && (
        <Card>
          <CardContent className="pt-4">
            <div className="flex flex-wrap gap-3">
              {Object.entries(summary.by_type).map(([type, amount]) => {
                const cfg = getTypeConfig(type);
                const Icon = cfg.icon;
                return (
                  <div key={type} className="flex items-center gap-1.5 text-sm">
                    <Icon className={`w-4 h-4 ${cfg.color}`} />
                    <span className="text-muted-foreground">{cfg.label}:</span>
                    <span className="font-medium">{formatCurrency(amount)}</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Fixed Costs Table */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <CardTitle className="text-base">Fixed Costs</CardTitle>
          <Button size="sm" onClick={openAdd} data-testid="add-fixed-cost-btn">
            <Plus className="w-4 h-4 mr-1" /> Add
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          {costs.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <HandCoins className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p>No fixed costs yet</p>
              <p className="text-sm mt-1">Add your utility bills, rent, and regular expenses</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Provider</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead className="hidden sm:table-cell">Frequency</TableHead>
                    <TableHead className="hidden sm:table-cell">Due</TableHead>
                    <TableHead>Payment</TableHead>
                    <TableHead className="w-[80px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {costs.map((c) => {
                    const cfg = getTypeConfig(c.type);
                    const Icon = cfg.icon;
                    const pmInfo = PAYMENT_METHODS.find(p => p.value === c.payment_method) || { label: c.payment_method };
                    const hasHistory = (c.rate_history || []).length > 1;
                    const isPending = c.effective_status === 'pending';
                    const isEnded = c.effective_status === 'ended';
                    const rowOpacity = (isPending || isEnded) ? 'opacity-50' : '';
                    return (
                      <React.Fragment key={c.id}>
                        <TableRow
                          className={`cursor-pointer hover:bg-accent/50 ${rowOpacity}`}
                          data-testid={`fixed-cost-row-${c.id}`}
                        >
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-2">
                              <Icon className={`w-4 h-4 ${cfg.color} shrink-0`} />
                              <div>
                                {c.provider}
                                {isPending && (
                                  <Badge variant="outline" className="ml-2 text-[10px] h-5 text-blue-600 border-blue-200 bg-blue-50" data-testid={`pending-badge-${c.id}`}>
                                    <CalendarClock className="w-3 h-3 mr-0.5" />Starts {new Date(c.start_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                                  </Badge>
                                )}
                                {isEnded && (
                                  <Badge variant="outline" className="ml-2 text-[10px] h-5 text-red-600 border-red-200 bg-red-50" data-testid={`ended-badge-${c.id}`}>
                                    <CircleOff className="w-3 h-3 mr-0.5" />Ended
                                  </Badge>
                                )}
                                {!isPending && !isEnded && c.end_date && (
                                  <Badge variant="outline" className="ml-2 text-[10px] h-5 text-amber-600 border-amber-200 bg-amber-50" data-testid={`ends-badge-${c.id}`}>
                                    Ends {new Date(c.end_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                                  </Badge>
                                )}
                              </div>
                            </div>
                            {c.notes && <p className="text-xs text-muted-foreground mt-0.5 truncate max-w-[200px]">{c.notes}</p>}
                          </TableCell>
                          <TableCell><span className="text-xs">{cfg.label}</span></TableCell>
                          <TableCell className="text-right font-medium">
                            <div>
                              {c.is_variable && <span className="text-muted-foreground">~</span>}{formatCurrency(c.amount)}
                              {c.is_variable && (
                                <Badge variant="outline" className="ml-1 text-[10px] h-4 text-purple-600 border-purple-200 bg-purple-50" data-testid={`variable-badge-${c.id}`}>
                                  Est.
                                </Badge>
                              )}
                              {c.frequency !== 'monthly' && (
                                <span className="text-xs text-muted-foreground block">
                                  ({c.is_variable ? '~' : ''}{formatCurrency(c.monthly_equivalent)}/mo)
                                </span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="hidden sm:table-cell capitalize text-sm">{c.frequency}</TableCell>
                          <TableCell className="hidden sm:table-cell text-sm">{c.payment_day}{getOrdinal(c.payment_day)}</TableCell>
                          <TableCell>
                            {c.auto_paid ? (
                              <Badge variant="outline" className="text-green-700 border-green-200 bg-green-50 text-xs" data-testid={`auto-badge-${c.id}`}>
                                <ArrowDownCircle className="w-3 h-3 mr-1" />{pmInfo.label}
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-amber-700 border-amber-200 bg-amber-50 text-xs" data-testid={`manual-badge-${c.id}`}>
                                <HandCoins className="w-3 h-3 mr-1" />{pmInfo.label}
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              {hasHistory && (
                                <Button
                                  variant="ghost" size="icon" className="h-7 w-7"
                                  onClick={() => setHistoryOpen(historyOpen === c.id ? null : c.id)}
                                  data-testid={`history-btn-${c.id}`}
                                >
                                  {historyOpen === c.id ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                </Button>
                              )}
                              <Button
                                variant="ghost" size="icon" className="h-7 w-7"
                                onClick={() => openEdit(c)}
                                data-testid={`edit-fixed-cost-${c.id}`}
                              >
                                <Pencil className="w-3.5 h-3.5" />
                              </Button>
                              <Button
                                variant="ghost" size="icon" className="h-7 w-7 text-destructive"
                                onClick={() => handleDelete(c.id)}
                                data-testid={`delete-fixed-cost-${c.id}`}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                        {hasHistory && historyOpen === c.id && (
                          <TableRow>
                            <TableCell colSpan={7} className="bg-muted/30 py-2 px-4">
                              <p className="text-xs font-medium mb-1">Rate History</p>
                              <div className="space-y-1">
                                {c.rate_history.map((rh, i) => (
                                  <div key={i} className="flex items-center gap-2 text-xs text-muted-foreground">
                                    <span className="font-medium">{formatCurrency(rh.amount)}</span>
                                    <span>from {rh.effective_from}</span>
                                    {rh.effective_to && <span>to {rh.effective_to}</span>}
                                    {!rh.effective_to && <Badge variant="outline" className="text-[10px] h-4">current</Badge>}
                                  </div>
                                ))}
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Fixed Cost' : 'Add Fixed Cost'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Provider</Label>
              <Input
                value={form.provider}
                onChange={(e) => setForm(f => ({ ...f, provider: e.target.value }))}
                placeholder="e.g. British Gas"
                data-testid="fc-provider-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm(f => ({ ...f, type: v }))}>
                  <SelectTrigger data-testid="fc-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(TYPE_CONFIG).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Amount (£)</Label>
                <Input
                  type="number" step="0.01" min="0"
                  value={form.amount}
                  onChange={(e) => setForm(f => ({ ...f, amount: e.target.value }))}
                  placeholder="280.00"
                  data-testid="fc-amount-input"
                />
              </div>
            </div>
            <label className="flex items-center gap-2 cursor-pointer" data-testid="fc-variable-toggle">
              <input
                type="checkbox"
                checked={form.is_variable}
                onChange={(e) => setForm(f => ({ ...f, is_variable: e.target.checked }))}
                className="rounded border-gray-300"
              />
              <div>
                <span className="text-sm font-medium">Variable amount</span>
                <p className="text-xs text-muted-foreground">Amount changes periodically (e.g. energy bills reviewed quarterly)</p>
              </div>
            </label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Frequency</Label>
                <Select value={form.frequency} onValueChange={(v) => setForm(f => ({ ...f, frequency: v }))}>
                  <SelectTrigger data-testid="fc-frequency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FREQUENCIES.map(fr => (
                      <SelectItem key={fr.value} value={fr.value}>{fr.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Payment Day</Label>
                <Input
                  type="number" min="1" max="28"
                  value={form.payment_day}
                  onChange={(e) => setForm(f => ({ ...f, payment_day: e.target.value }))}
                  data-testid="fc-payment-day-input"
                />
              </div>
            </div>
            <div>
              <Label>Payment Method</Label>
              <Select value={form.payment_method} onValueChange={(v) => setForm(f => ({ ...f, payment_method: v }))}>
                <SelectTrigger data-testid="fc-payment-method-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHODS.map(pm => (
                    <SelectItem key={pm.value} value={pm.value}>
                      {pm.label} {pm.auto && '(auto)'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {PAYMENT_METHODS.find(p => p.value === form.payment_method)?.auto ? (
                <p className="text-xs text-green-600 mt-1">This will be auto-confirmed each month — no action needed</p>
              ) : (
                <p className="text-xs text-amber-600 mt-1">You'll be reminded to make this payment each month</p>
              )}
            </div>
            <Separator />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>First Payment Date</Label>
                <Input
                  type="date"
                  value={form.start_date}
                  onChange={(e) => setForm(f => ({ ...f, start_date: e.target.value }))}
                  data-testid="fc-start-date-input"
                />
                <p className="text-xs text-muted-foreground mt-1">When this cost begins</p>
              </div>
              <div>
                <Label>End Date <span className="text-muted-foreground font-normal">(optional)</span></Label>
                <Input
                  type="date"
                  value={form.end_date}
                  onChange={(e) => setForm(f => ({ ...f, end_date: e.target.value }))}
                  data-testid="fc-end-date-input"
                />
                <p className="text-xs text-muted-foreground mt-1">Leave empty if ongoing</p>
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Input
                value={form.notes}
                onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Account number, contract details..."
                data-testid="fc-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} data-testid="fc-save-btn">
              {editing ? 'Save Changes' : 'Add Fixed Cost'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

function getOrdinal(n) {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return s[(v - 20) % 10] || s[v] || s[0];
}

export default FixedCostsTab;
